import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  CircularProgress,
  Alert as MuiAlert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useAuthHeader } from '@/hooks/useAuth';
import { getProductMasterPreview } from '@/lib/api/product-master';
import { getProductGroupTypes } from '@/lib/api/product-group-type';
import {
  getLatestProductMasterDefaults,
  startProductMasterExtraction,
} from '@/lib/api/analysis-data-extraction';
import type { ProductMasterPreview as ProductMasterPreviewData } from '@/lib/types/feature/product-master';
import type {
  ProductGroupSelection,
  ProductMasterColumnGroup,
} from '@/lib/types/feature/analysis-data-extraction';
import type { ProductGroupType } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** 選択できる商品グループタイプの上限（バックエンドの縦持ちと同じ） */
const MAX_SELECTED_TYPES = 15;
/** プレビューの表示件数（バックエンドの PREVIEW_LIMIT と同じ。案内文にのみ使う） */
const PREVIEW_ROW_LIMIT = 100;
/** 再入庫日の閾値θの既定値（1点でも増えれば再入庫） */
const DEFAULT_RESTOCK_THRESHOLD = '0';
/** 集計階層の選択肢。null は最下層（打ち切らずフルパス） */
const DEPTH_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: '最下層' },
  { value: 1, label: '1階層' },
  { value: 2, label: '2階層' },
  { value: 3, label: '3階層' },
  { value: 4, label: '4階層' },
  { value: 5, label: '5階層' },
];

/** 出力を省ける列のまとまり。並びはCSVの列順に合わせる */
const COLUMN_GROUP_OPTIONS: {
  value: ProductMasterColumnGroup;
  label: string;
  description: string;
}[] = [
  {
    value: 'price',
    label: '価格（定価・現売価・原価）',
    description: 'SKU・品番・親SKUの3階層ぶんで9列',
  },
  { value: 'product_code', label: '品番（ID・名称）', description: '2列' },
  { value: 'parent_sku', label: '親SKU（ID・名称）', description: '2列' },
  {
    value: 'lifecycle',
    label: '日付項目（販売開始日・初入庫日・再入庫日など）',
    description: '8列。省くと全期間の集計が不要になるため実行時間も大きく短くなります',
  },
];

interface ProductMasterPreviewProps {
  account: { id: string; code: string; name: string };
  onSubmitted?: () => void;
}

/**
 * 商品マスタ抽出のプレビュー画面（FKV3-12589）
 * 先頭100件を同期APIで取得しMUIテーブルに表示する。全件CSVは非同期ジョブとして別途受け付ける。
 */
export function ProductMasterPreview({ account, onSubmitted }: ProductMasterPreviewProps) {
  const { getAuthHeader } = useAuthHeader();

  const [preview, setPreview] = useState<ProductMasterPreviewData | null>(null);
  // プレビューは明示操作でしか走らないため、初期状態は「未実行」（読み込み中ではない）
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 再入庫日の閾値θ。入力途中の空文字を許すため文字列で持ち、送信時に数値へ直す
  const [restockThreshold, setRestockThreshold] = useState(DEFAULT_RESTOCK_THRESHOLD);
  // 母集団を分析対象SKUだけに絞るか。既定は全SKU（FKV3-12589 の要件が全SKUだったため）
  const [analysisTargetOnly, setAnalysisTargetOnly] = useState(false);
  // 販売実績も在庫実績も無いSKUを出力から除くか。既定は全て出力（従来と同じCSVになる）
  const [excludeSkusWithoutActivity, setExcludeSkusWithoutActivity] = useState(false);
  // 出力する列を絞り込むか。OFFのあいだは列の選択肢自体を畳んでおく（既定は全列出力）
  const [restrictColumns, setRestrictColumns] = useState(false);
  // 出力から省く列のまとまり。既定は全列出力（従来と同じCSVになる）
  const [excludedColumnGroups, setExcludedColumnGroups] = useState<ProductMasterColumnGroup[]>([]);
  // プレビューに反映済みの列構成（isDirty の判定に使う）
  const [appliedExcludedColumnGroups, setAppliedExcludedColumnGroups] = useState<
    ProductMasterColumnGroup[]
  >([]);
  // 結果をZIPに圧縮するか。既定はCSV直（そのまま開ける方が扱いやすいため）
  const [compressAsZip, setCompressAsZip] = useState(false);
  // ライフサイクル指標のキャッシュを使わず再計算するか。都度判断する検証用なので前回値は引き継がない
  const [skipLifecycleCache, setSkipLifecycleCache] = useState(false);
  // プレビューを取得した時点の母集団の絞り込み（isDirty の判定に使う）
  const [appliedAnalysisTargetOnly, setAppliedAnalysisTargetOnly] = useState(false);

  const [productGroupTypes, setProductGroupTypes] = useState<ProductGroupType[]>([]);
  // 画面で編集中の選択。空配列は「全タイプ×最下層」（バックエンドの既定と同じ意味）
  const [selections, setSelections] = useState<ProductGroupSelection[]>([]);
  // プレビューに実際に反映済みの選択。プレビュー1回がAthenaクエリ1本で数十秒かかるため、
  // タブを開いた時点でも選択を触った時点でも自動では投げず、明示操作でのみ取得する
  const [appliedSelections, setAppliedSelections] = useState<ProductGroupSelection[]>([]);

  // 現在のアカウントコード（前アカウントの遅延レスポンス破棄・アカウント切替検知の両方に使う）。
  // レンダー中に直接書き換える（useEffectでの更新はコミット後まで遅延するため、その間に
  // 前アカウントのレスポンスが解決すると古いrefの値で stale チェックを通過してしまう）。
  const accountCodeRef = useRef(account.code);
  if (account.code !== accountCodeRef.current) {
    accountCodeRef.current = account.code;
    setPreview(null);
    setError(null);
    // 切替後も自動では取得しないため、読み込み中ではなく未実行の状態に戻す
    setLoading(false);
    // 商品グループタイプはアカウント固有なので選択も破棄する
    setProductGroupTypes([]);
    setSelections([]);
    setAppliedSelections([]);
    // 閾値θはアカウントごとに水準が違う（在庫コピーのあるアカウントは大きめ）ため引き継がない
    setRestockThreshold(DEFAULT_RESTOCK_THRESHOLD);
    setAnalysisTargetOnly(false);
    setExcludeSkusWithoutActivity(false);
    setRestrictColumns(false);
    setExcludedColumnGroups([]);
    setAppliedExcludedColumnGroups([]);
    setCompressAsZip(false);
    setSkipLifecycleCache(false);
  }

  const fetchPreview = async (target: ProductGroupSelection[]) => {
    const requestCode = account.code;
    try {
      setLoading(true);
      setError(null);
      const authHeaders = getAuthHeader();
      const data = await getProductMasterPreview(
        requestCode,
        target,
        authHeaders,
        analysisTargetOnly,
        excludedColumnGroups,
      );
      if (accountCodeRef.current !== requestCode) return;
      setPreview(data);
      setAppliedSelections(target);
      setAppliedAnalysisTargetOnly(analysisTargetOnly);
      setAppliedExcludedColumnGroups(excludedColumnGroups);
    } catch (e) {
      if (accountCodeRef.current !== requestCode) return;
      const message = e instanceof Error ? e.message : 'プレビューの取得に失敗しました';
      setError(message);
    } finally {
      if (accountCodeRef.current === requestCode) setLoading(false);
    }
  };

  // 商品グループタイプのマスタだけ先に取る。プレビュー本体は取らない
  // （abdi は約56万SKUあり、プレビューのAthenaクエリだけで数十秒かかる。タブを開くたびに
  // 走らせると、見るつもりがないときも待たされ、CSVダウンロードにも着手できなかった）。
  // リセットは上のレンダー中の調整で完了済み
  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    getProductGroupTypes(account.code, getAuthHeader())
      .then((types) => {
        if (!cancelled) setProductGroupTypes(types);
      })
      .catch(() => {
        // タイプが取れなくても全タイプ出力のCSVは出せるため、ここでは画面を止めない
        if (!cancelled) setProductGroupTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 閾値θ・母集団の絞り込み・出力形式は前回実行値を初期値にする（商品グループ選択は従来どおり毎回選ぶ）。
  // ハニーズ500・ゾフ200のようにアカウント固有の水準があり、毎回入れ直すのは事故のもとになる。
  // キャッシュを使わない指定だけは復元しない（毎回再計算すると実行時間と課金が無駄に増えるため）
  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    getLatestProductMasterDefaults(account.code, getAuthHeader())
      .then((defaults) => {
        if (cancelled || !defaults.parameters) return;
        setRestockThreshold(String(defaults.parameters.restock_threshold));
        setAnalysisTargetOnly(defaults.parameters.analysis_target_only);
        // 旧ジョブ（出力形式・列指定を持たない頃）の前回条件は undefined で返る
        setCompressAsZip(defaults.parameters.compress_as_zip ?? false);
        const excluded = defaults.parameters.excluded_column_groups ?? [];
        setExcludedColumnGroups(excluded);
        setRestrictColumns(excluded.length > 0);
        setExcludeSkusWithoutActivity(defaults.parameters.exclude_skus_without_activity ?? false);
      })
      .catch(() => {
        // 前回値が取れなくても既定0で実行できるため、ここでは画面を止めない
      });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 選択済みタイプの位置（0始まり）。選択判定と列順表示に使う
  const selectedIndexById = useMemo(
    () => new Map(selections.map((s, index) => [s.type_id, index])),
    [selections],
  );
  const selectedTypeNames = useMemo(
    () =>
      selections.map((s) => productGroupTypes.find((t) => t.id === s.type_id)?.name ?? s.type_id),
    [selections, productGroupTypes],
  );
  // 編集中の条件がプレビューに反映済みかどうか（未反映なら更新を促す）。
  // 母集団の絞り込みも含める（プレビューの「全◯件」が変わるため）
  const isDirty = useMemo(
    () =>
      JSON.stringify(selections) !== JSON.stringify(appliedSelections) ||
      analysisTargetOnly !== appliedAnalysisTargetOnly ||
      JSON.stringify(excludedColumnGroups) !== JSON.stringify(appliedExcludedColumnGroups),
    [
      selections,
      appliedSelections,
      analysisTargetOnly,
      appliedAnalysisTargetOnly,
      excludedColumnGroups,
      appliedExcludedColumnGroups,
    ],
  );

  /** 列の絞り込みを開閉する。閉じたときは除外指定を捨てて全列出力に戻す */
  const toggleRestrictColumns = (checked: boolean) => {
    setRestrictColumns(checked);
    if (!checked) setExcludedColumnGroups([]);
  };

  /** 列のまとまりの出力有無を切り替える（チェック＝出力する。内部では省く側を持つ） */
  const toggleColumnGroup = (group: ProductMasterColumnGroup) => {
    setExcludedColumnGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group],
    );
  };

  const toggleType = (typeId: string) => {
    setSelections((prev) => {
      const index = prev.findIndex((s) => s.type_id === typeId);
      if (index >= 0) return prev.filter((s) => s.type_id !== typeId);
      if (prev.length >= MAX_SELECTED_TYPES) return prev;
      return [...prev, { type_id: typeId, depth: null }];
    });
  };

  const changeDepth = (typeId: string, depth: number | null) => {
    setSelections((prev) => prev.map((s) => (s.type_id === typeId ? { ...s, depth } : s)));
  };

  /** 列順を入れ替える（上限15件なのでドラッグ&ドロップは用意しない） */
  const moveSelection = (index: number, direction: -1 | 1) => {
    setSelections((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleDownload = async () => {
    const threshold = Number(restockThreshold);
    if (!Number.isInteger(threshold) || threshold < 0) {
      const message = '再入庫日の閾値は0以上の整数で入力してください';
      setError(message);
      toast.error(message);
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const authHeaders = getAuthHeader();
      await startProductMasterExtraction(
        account.code,
        {
          product_group_selections: selections,
          restock_threshold: threshold,
          analysis_target_only: analysisTargetOnly,
          excluded_column_groups: excludedColumnGroups,
          exclude_skus_without_activity: excludeSkusWithoutActivity,
          compress_as_zip: compressAsZip,
          skip_lifecycle_cache: skipLifecycleCache,
        },
        authHeaders,
      );
      toast.success('抽出ジョブを受け付けました。実行ログで進捗を確認できます');
      onSubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : '抽出ジョブの実行に失敗しました';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          商品マスタ抽出
        </Typography>

        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        {/* 再入庫日の判定閾値θ。全SKU共通で、CSV出力時にだけ使う */}
        <Box sx={{ mb: 2 }}>
          <Label htmlFor="restock-threshold">
            再入庫日の閾値（みなし入庫数がこの値を超えた日）
          </Label>
          <div className="mt-2 flex items-center gap-3">
            <Input
              id="restock-threshold"
              type="number"
              min={0}
              step={1}
              value={restockThreshold}
              onChange={(e) => setRestockThreshold(e.target.value)}
              className="w-32"
            />
            <span className="text-xs text-gray-500">
              0は「1点でも増えれば再入庫」。在庫連携が日次でないアカウントでは、在庫が前日と同値のまま
              販売だけが積まれる日を拾ってしまうため、日次販売数より大きい値を指定してください
            </span>
          </div>
        </Box>

        {/* 出力する商品グループタイプと集計階層。未選択は全タイプ×最下層 */}
        <Box sx={{ mb: 2 }}>
          <Label>出力する商品グループ（未選択なら全タイプを最下層まで出力します）</Label>
          <div className="mt-2 rounded-md border p-3">
            {selections.length > 0 && (
              <div className="mb-3 rounded-md bg-gray-50 p-2">
                <div className="mb-1 text-xs text-gray-600">
                  現在の列順（CSVではこの順で左から並びます）
                </div>
                <ol className="flex flex-wrap items-center gap-1">
                  {selections.map((selection, index) => {
                    const name = selectedTypeNames[index];
                    return (
                      <li key={selection.type_id} className="flex items-center gap-1">
                        {index > 0 && <span className="text-gray-400">→</span>}
                        <span className="flex items-center gap-1 rounded border bg-white px-1.5 py-0.5 text-xs">
                          <span className="text-gray-400">{index + 1}.</span>
                          <span>{name}</span>
                          <select
                            aria-label={`${name}の集計階層`}
                            value={selection.depth === null ? '' : String(selection.depth)}
                            onChange={(e) =>
                              changeDepth(
                                selection.type_id,
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                            className="rounded border px-1 py-0.5 text-xs"
                          >
                            {DEPTH_OPTIONS.map((option) => (
                              <option
                                key={option.label}
                                value={option.value === null ? '' : String(option.value)}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            aria-label={`${name}を左へ移動`}
                            disabled={index === 0}
                            onClick={() => moveSelection(index, -1)}
                            className="px-0.5 text-gray-500 disabled:opacity-30"
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            aria-label={`${name}を右へ移動`}
                            disabled={index === selections.length - 1}
                            onClick={() => moveSelection(index, 1)}
                            className="px-0.5 text-gray-500 disabled:opacity-30"
                          >
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {productGroupTypes.length === 0 ? (
              <div className="text-sm text-gray-500">商品グループタイプがありません</div>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {productGroupTypes.map((type) => {
                  const order = selectedIndexById.get(type.id);
                  const checked = order !== undefined;
                  const disabled = !checked && selections.length >= MAX_SELECTED_TYPES;
                  return (
                    <Chip
                      key={type.id}
                      label={checked ? `${order + 1}. ${type.name}` : type.name}
                      color={checked ? 'primary' : 'default'}
                      variant={checked ? 'filled' : 'outlined'}
                      onClick={() => toggleType(type.id)}
                      disabled={disabled}
                      sx={{ height: 28 }}
                    />
                  );
                })}
              </Box>
            )}

            <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
              <span>
                選択数: {selections.length} / {MAX_SELECTED_TYPES}
              </span>
              {selections.length >= MAX_SELECTED_TYPES && (
                <span className="text-amber-600">
                  上限に達しています。別のタイプを選ぶには、選択済みのタイプを外してください
                </span>
              )}
            </div>
          </div>
        </Box>

        {/* 出力対象の絞り込み。列・母集団の指定をひとまとめにする（個別に並べると圧迫感が出る） */}
        <Box sx={{ mb: 2 }}>
          <Label>出力対象を絞り込む</Label>
          <div className="mt-2 space-y-3 rounded-md border p-3">
            {/* 列の絞り込み。既定では畳んでおき、チェックしたときだけ選択肢を出す */}
            <div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="restrict-columns"
                  checked={restrictColumns}
                  onCheckedChange={(checked) => toggleRestrictColumns(checked === true)}
                />
                <div>
                  <Label htmlFor="restrict-columns" className="font-normal">
                    出力するカラムを絞り込む
                  </Label>
                  <div className="mt-0.5 text-xs text-gray-500">
                    SKU_IDとSKU名は常に出力します。列を減らすとファイルサイズも小さくなるため、
                    ダウンロードできない場合やAIに読み込ませる場合に使ってください。
                  </div>
                </div>
              </div>

              {restrictColumns && (
                <div className="ml-6 mt-2 space-y-2 border-l pl-3">
                  <div className="text-xs text-gray-600">
                    チェックを外した項目はCSVに出力しません
                  </div>
                  {COLUMN_GROUP_OPTIONS.map((option) => (
                    <div key={option.value} className="flex items-start gap-2">
                      <Checkbox
                        id={`column-group-${option.value}`}
                        checked={!excludedColumnGroups.includes(option.value)}
                        onCheckedChange={() => toggleColumnGroup(option.value)}
                      />
                      <div>
                        <Label htmlFor={`column-group-${option.value}`} className="font-normal">
                          {option.label}
                        </Label>
                        <div className="mt-0.5 text-xs text-gray-500">{option.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 母集団の絞り込み。プレビューとCSVの両方に効く */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="analysis-target-only"
                checked={analysisTargetOnly}
                onCheckedChange={(checked) => setAnalysisTargetOnly(checked === true)}
              />
              <div>
                <Label htmlFor="analysis-target-only" className="font-normal">
                  分析対象SKUのみ出力する
                </Label>
                <div className="mt-0.5 text-xs text-gray-500">
                  他メニュー（日別 店舗×SKU、週次 店舗/倉庫×商品グループ 等）はすべて分析対象SKUに
                  絞られています。それらの分析用データと突き合わせる用途でチェックしてください。
                  未チェックでは全SKUを出力するため、初入庫日・再入庫日が構造的に空欄になる
                  分析対象外SKUも含まれます。
                </div>
              </div>
            </div>

            {/* 実績の無いSKUの除外。CSVにだけ効く（プレビューは日付を計算しないため） */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="exclude-skus-without-activity"
                checked={excludeSkusWithoutActivity}
                onCheckedChange={(checked) => setExcludeSkusWithoutActivity(checked === true)}
              />
              <div>
                <Label htmlFor="exclude-skus-without-activity" className="font-normal">
                  販売実績・在庫実績がないSKUを出力しない
                </Label>
                <div className="mt-0.5 text-xs text-gray-500">
                  販売開始日と初入庫日がどちらも空になるSKU（販売も入荷も一度も記録がないもの）を
                  出力から除きます。他の日付項目もすべて空になる行なので、除いても失われる情報は
                  SKU名や商品グループだけです。
                </div>
                {excludeSkusWithoutActivity && (
                  <div className="mt-1 text-xs text-amber-600">
                    この絞り込みはプレビューには反映されません（プレビューでは日付を計算しないため）。
                    CSVの行数はプレビューの「全◯件」より少なくなります。
                    また、日付項目の出力を外していても判定のために計算するため、実行時間は短くなりません。
                  </div>
                )}
              </div>
            </div>
          </div>
        </Box>

        {/* ここまでの設定でジョブを受け付ける。プレビューの取得状況では止めない
            （別のAthenaクエリを非同期ジョブで流すだけで、プレビューの完了を待つ必要がない） */}
        <Box sx={{ mb: 3 }}>
          <Button onClick={handleDownload} disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {compressAsZip ? '全件ZIPダウンロード' : '全件CSVダウンロード'}
          </Button>
          <div className="mt-2 flex items-start gap-2">
            <Checkbox
              id="compress-as-zip"
              checked={compressAsZip}
              onCheckedChange={(checked) => setCompressAsZip(checked === true)}
            />
            <div>
              <Label htmlFor="compress-as-zip" className="font-normal">
                ZIP形式で圧縮してダウンロードする
              </Label>
              <div className="mt-0.5 text-xs text-gray-500">
                SKU数や商品グループの列数が多いアカウントでは結果CSVが数百MBになり、ダウンロードが
                途中で終わらないことがあります。その場合にチェックしてください（中身はCSV1本で、
                解凍後のファイル名はチェックしない場合と同じです）。
              </div>
            </div>
          </div>
        </Box>

        {/* プレビューは常に明示操作で取得する（タブを開いた時点では走らせない） */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPreview(selections)}
            disabled={loading}
          >
            {preview ? 'プレビューを更新' : 'プレビューを表示'}
          </Button>
          {preview && isDirty && !loading && (
            <span className="text-xs text-amber-600">
              条件がプレビューに反映されていません（CSVには現在の条件が使われます）
            </span>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>プレビューを読み込み中...</Typography>
          </Box>
        ) : preview && preview.rows.length > 0 ? (
          <>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              全{preview.total_count.toLocaleString()}件中 {preview.rows.length}件を表示中
            </Typography>
            {/* 日付3項目は全期間スキャンが要るため、プレビューでは計算せず列だけ出す */}
            <MuiAlert severity="info" sx={{ mb: 1 }}>
              販売開始日・初入庫日・再入庫日はCSV出力時に計算します（プレビューでは常に空欄です）。
            </MuiAlert>
            <TableContainer
              component={Paper}
              sx={{
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTable-root': { minWidth: 1200 },
              }}
            >
              <Table size="small" sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    {preview.columns.map((column) => (
                      <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((value, columnIndex) => (
                        <TableCell key={preview.columns[columnIndex]} sx={{ whiteSpace: 'nowrap' }}>
                          {value ?? '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : preview ? (
          <MuiAlert severity="info">商品マスタデータが見つかりませんでした。</MuiAlert>
        ) : (
          // 未実行の状態。取得に時間がかかることと、CSVは待たずに落とせることを明示する
          !error && (
            <MuiAlert severity="info">
              プレビューはまだ取得していません。「プレビューを表示」を押すと先頭
              {PREVIEW_ROW_LIMIT}件を取得します（SKU数が多いアカウントでは数十秒かかります）。
              全件CSVダウンロードはプレビューを待たずに実行できます。
            </MuiAlert>
          )
        )}

        {/* ライフサイクル指標のキャッシュ回避。Athenaの再スキャンで課金が発生するため常用させたくない。
            プレビューより下に置き、注記も畳んで目立たせない */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <div className="flex items-start gap-2">
            <Checkbox
              id="skip-lifecycle-cache"
              checked={skipLifecycleCache}
              onCheckedChange={(checked) => setSkipLifecycleCache(checked === true)}
            />
            <div>
              <Label htmlFor="skip-lifecycle-cache" className="font-normal text-xs text-gray-500">
                キャッシュを使わず再計算する（通常は不要）
              </Label>
              {skipLifecycleCache && (
                <div className="mt-1 text-xs text-amber-600">
                  日付項目は同じ条件（データの取込日時・再入庫日の閾値・EC店舗設定）での再計算を避けるため
                  結果を保存して使い回しています。条件を変えれば自動で計算し直されるため通常は不要です。
                  チェックすると毎回Athenaで再集計するため、実行時間が延び、スキャン量に応じた課金が発生します。
                </div>
              )}
            </div>
          </div>
        </Box>
      </CardContent>
    </Card>
  );
}
